/**
 * 看板のカードに指を載せたときに出す補足 — 業務フローの型（メイン ／ サブ）・所属・未翻訳。
 *
 * **カードには名前と変更の印だけを見せ、補足はカードの `title`（`LaneCard.hint`）に移す**（Dhik 2026-09-29「本当に
 * 出すべき情報を 1 or 2 個（できれば 1 個）にするぐらい情報を絞ってほしい」）。前は札（メイン ／
 * サブ・未翻訳）と下段（アクター ／ ドメイン）をカードに並べていた。
 *
 * **業務マップと案件の変更一覧が同じ補足を使う**（引継書 §3.1「業務マップと同じ並び」）。画面ごとに
 * 書くと、同じ業務フローが画面によって「メイン」と出たり出なかったりする。
 *
 * 業務フローの呼び名は メインフロー ／ サブフロー（引継書 §2「呼び名」）。サブフローの役割（入れる ／
 * 出す ／ 割り込み）は出さない — 何の情報か読めず、フローの図を見れば分かる（Dhik の指示
 * 2026-09-28）。型の欄が読めない業務フローは「サブ」にする — メインで埋めると、型の欠けた業務フローが
 * 1 ステージにメインが 2 本在るように読める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 看板のカードの title に載せる補足（業務フローの型・所属・未翻訳）を 1 行の文字にする。
 * @graph-connects none
 */
import type { BusinessMapFlowRole } from '@cortex/domain-spec-console';

/** 文言を引く関数。 @graph-connects none */
type Translate = (key: string) => string;

/**
 * 業務フローの型の呼び名。メインフローは「メイン」、それ以外（型が読めないものを含む）は「サブ」。
 *
 * @param role 業務フローの型。欄が読めなければ null
 * @param t 文言
 * @returns 型の呼び名
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの型を、メインなら「メイン」、それ以外なら「サブ」の呼び名にする。
 * @graph-connects none
 */
export function flowKindLabel(role: BusinessMapFlowRole | null, t: Translate): string {
  return t(role === 'main' ? 'overview.flowMain' : 'overview.flowSub');
}

/**
 * 名前はあるが選んだ言語の訳が欠けているときの「未翻訳」。名前が空の行は見出しそのものが
 * 「未翻訳」「未設定」と言っている（`displayLabel`）ので重ねない（`SpecLabelBadges` と同じ条件）。
 *
 * @param label 選んだ言語へ畳んだ表示名
 * @param untranslated 選んだ言語に訳が入っていないか
 * @param t 文言
 * @returns 「未翻訳」。添えない行は null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 名前はあるが訳の欠けたカードにだけ、カードの補足へ「未翻訳」を添える。
 * @graph-connects none
 */
export function untranslatedHint(
  label: string | null,
  untranslated: boolean,
  t: Translate,
): string | null {
  return (label ?? '') !== '' && untranslated ? t('spec.untranslated') : null;
}

/**
 * カードの補足を 1 行にする。**1 つも無ければ `undefined`**（`title` を付けない）。
 *
 * 参照が張られていない（`null`）と、参照は在るが名前が入っていない（空文字）の両方を落とす。
 * 本番の要素表は後者を返すことがあり、落とさずに繋ぐと区切りだけが残る。
 *
 * @param parts 並べる補足
 * @returns ` / ` で繋いだ 1 行。1 つも無ければ undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 看板のカードの title に載せる補足を、空の物を落として 1 行に繋ぐ。
 * @graph-connects none
 */
export function laneCardHint(parts: readonly (string | null | undefined)[]): string | undefined {
  const shown = parts.filter(
    (part): part is string => part !== null && part !== undefined && part !== '',
  );
  return shown.length === 0 ? undefined : shown.join(' / ');
}
